import { useEffect, useMemo, useState } from "react";
import { Search, RefreshCw, Network, ChevronRight, X, ArrowDownToLine, ArrowUpFromLine, Wallet, CheckCircle2, XCircle, Clock, ExternalLink, Settings, Coins, History as HistoryIcon, FileDown, Calendar, PlayCircle, TrendingUp, AlertCircle, Activity } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import { fmtBDT } from "../lib/format";
import { toast } from "sonner";

/**
 * Admin · Referral Network
 *
 * Smart 3-level drill-down view of the user-to-user invite graph:
 *   1. Network table   — every user who has referred ≥1 other user
 *   2. Referees drawer — list of users they directly referred + per-user totals
 *   3. Transactions    — full deposit/withdrawal history for a single referee
 *
 * All financial figures (deposit_total, withdraw_total) are aggregated
 * server-side from the `deposits` / `withdrawals` collections, so the page
 * is cheap to render even for thousands of users.
 */

const fmtBD = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Dhaka",
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
};

export default function ReferralNetwork() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total_referrers: 0, total_referees: 0, total_deposits: 0, total_withdrawals: 0, net_value: 0, total_loss: 0, commission_pending: 0, commission_paid: 0 });
  const [lossCfg, setLossCfg] = useState({ enabled: true, pct: 10, min_loss_threshold: 0, min_payout: 0 });
  const [showSettings, setShowSettings] = useState(false);
  const [historyDrawer, setHistoryDrawer] = useState(null); // {referrer, items, total_paid}
  const [payingId, setPayingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drill, setDrill] = useState(null); // {referrer, upline, referees, totals}
  const [drillLoading, setDrillLoading] = useState(false);
  const [txDrawer, setTxDrawer] = useState(null); // {user, items, stats}
  const [txLoading, setTxLoading] = useState(false);

  // ---- NEW: NGR settlement state ----
  const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const yesterdayISO = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [ngrCfg, setNgrCfg] = useState({ enabled: true, pct: 10 });
  const [ngrDate, setNgrDate] = useState(yesterdayISO());
  const [ngrReport, setNgrReport] = useState(null); // {settlement_date, summary, items}
  const [ngrSettlements, setNgrSettlements] = useState([]);
  const [ngrLoading, setNgrLoading] = useState(false);
  const [ngrRunning, setNgrRunning] = useState(false);
  const [showNgrSettings, setShowNgrSettings] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/referrals/network", { params: { limit: 200 } });
      setRows(data.items || []);
      setStats(data.stats || {});
      if (data.loss_commission) setLossCfg(data.loss_commission);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load referral network");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ---- NGR: load config, settlement logs, and report for selected date ----
  const loadNgr = async (date = ngrDate) => {
    setNgrLoading(true);
    try {
      const [cfgR, repR, setR] = await Promise.all([
        api.get("/admin/referral-ngr/config"),
        api.get("/admin/referral-ngr/daily-reports", { params: { date, limit: 200 } }),
        api.get("/admin/referral-ngr/settlements", { params: { limit: 14 } }),
      ]);
      setNgrCfg({ enabled: !!cfgR.data.enabled, pct: Number(cfgR.data.pct || 0) });
      setNgrReport(repR.data);
      setNgrSettlements(setR.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load NGR data");
    } finally {
      setNgrLoading(false);
    }
  };
  useEffect(() => { loadNgr(); /* on mount only — date changes use the button */ }, []);

  const runNgrNow = async () => {
    if (!window.confirm(`Run NGR settlement for ${ngrDate}?\n\nIdempotent — won't double-credit if already settled.`)) return;
    setNgrRunning(true);
    try {
      const { data } = await api.post("/admin/referral-ngr/run-now", null, { params: { date: ngrDate } });
      if (data.already_settled) {
        toast.info(`Already settled on ${ngrDate}`);
      } else {
        toast.success(`Done · credited ${data.commissions_credited || 0} referrers · ৳${(data.total_commission_paid || 0).toFixed(2)}`);
      }
      await loadNgr(ngrDate);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Settlement failed");
    } finally {
      setNgrRunning(false);
    }
  };

  const saveNgrCfg = async (next) => {
    try {
      const { data } = await api.patch("/admin/referral-ngr/config", next);
      setNgrCfg({ enabled: !!data.enabled, pct: Number(data.pct || 0) });
      toast.success("NGR config updated");
      setShowNgrSettings(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const saveSettings = async (next) => {
    try {
      const { data } = await api.put("/admin/referrals/loss-commission/config", next);
      setLossCfg({
        enabled: !!data.enabled,
        pct: Number(data.pct || 0),
        min_loss_threshold: Number(data.min_loss_threshold || 0),
        min_payout: Number(data.min_payout || 0),
      });
      toast.success("Referral commission settings saved");
      setShowSettings(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save settings");
    }
  };

  const payCommission = async (row) => {
    if (!row.referrer_id) return;
    if (!window.confirm(`Pay ৳${(row.commission_pending || 0).toFixed(2)} commission to ${row.username}?\n\nThis will be credited to their wallet immediately and cannot be undone.`)) return;
    setPayingId(row.referrer_id);
    try {
      const { data } = await api.post(`/admin/referrals/${row.referrer_id}/loss-commission/pay`, {});
      toast.success(`৳${(data.amount_paid || 0).toFixed(2)} credited to ${row.username}. New balance ৳${(data.new_balance || 0).toFixed(2)}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally {
      setPayingId(null);
    }
  };

  const openHistory = async (row) => {
    setHistoryDrawer({ referrer: row, items: [], total_paid: 0, loading: true });
    try {
      const { data } = await api.get(`/admin/referrals/${row.referrer_id}/loss-commission/history`);
      setHistoryDrawer({ referrer: row, items: data.items || [], total_paid: data.total_paid || 0, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
      setHistoryDrawer(null);
    }
  };

  // Build "last 12 months" + current month options for the PDF download dropdown
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
      opts.push({ v, label: i === 0 ? `${label} (current)` : label });
    }
    return opts;
  }, []);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [downloading, setDownloading] = useState(false);

  const downloadMonthlyPdf = async () => {
    setDownloading(true);
    try {
      const params = selectedMonth ? { month: selectedMonth } : {};
      const res = await api.get(`/admin/referrals/monthly-report.pdf`, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const m = selectedMonth || (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      })();
      a.href = url;
      a.download = `referral-summary-${m}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter((r) =>
          [r.username, r.full_name, r.refer_code, r.phone, r.email, r.player_id]
            .some((v) => (v || "").toString().toLowerCase().includes(q))
        )
      : rows;
    // Newest-first by created_at; fall back to highest commission/deposit for legacy rows
    const arr = [...base];
    arr.sort((a, b) => {
      const av = String(a?.created_at || "");
      const bv = String(b?.created_at || "");
      if (av || bv) return bv.localeCompare(av);
      return (b?.commission_paid || 0) + (b?.deposit_total || 0)
           - (a?.commission_paid || 0) - (a?.deposit_total || 0);
    });
    return arr;
  }, [rows, search]);

  // Pagination — 12 per page (newest at top)
  const PAGE_SIZE = 12;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, filtered.length]);
  const visibleFiltered = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const openDrill = async (row) => {
    setDrillLoading(true);
    setDrill({ referrer: { ...row, user_id: row.referrer_id }, referees: [], totals: {}, upline: null });
    try {
      const { data } = await api.get(`/admin/referrals/${row.referrer_id}`);
      setDrill(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load referees");
      setDrill(null);
    } finally {
      setDrillLoading(false);
    }
  };

  const openTransactions = async (referee) => {
    setTxLoading(true);
    setTxDrawer({ user: referee, items: [], stats: {} });
    try {
      const { data } = await api.get(`/admin/referrals/user/${referee.user_id}/transactions`);
      setTxDrawer({ user: referee, ...data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load transactions");
      setTxDrawer(null);
    } finally {
      setTxLoading(false);
    }
  };

  return (
    <div data-testid="admin-referral-network">
      <PageHeader
        title="Referral System · NGR Commission"
        subtitle={`Daily NGR-based commission @ ${ngrCfg.pct}% credited every রাত ১২টা (00:00 Asia/Dhaka)`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="h-9 px-2 border border-gray-200 rounded-sm text-[13px] bg-white"
              data-testid="ref-pdf-month-select"
              title="Select month for PDF report"
            >
              {monthOptions.map((o) => (
                <option key={o.v} value={o.v === monthOptions[0].v ? "" : o.v}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={downloadMonthlyPdf}
              disabled={downloading}
              className="h-9 px-3 inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white rounded-sm text-[13px] font-semibold disabled:opacity-50"
              data-testid="download-monthly-pdf"
              title="Download per-referrer monthly summary PDF (one click)"
            >
              <FileDown size={13} /> {downloading ? "Generating…" : "Download Monthly PDF"}
            </button>
            <button
              onClick={() => setShowNgrSettings(true)}
              className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-emerald-300 hover:text-emerald-700 rounded-sm text-[13px]"
              data-testid="open-ngr-settings"
            >
              <Settings size={13} /> NGR Config ({ngrCfg.pct}%)
            </button>
            <button
              onClick={() => { load(); loadNgr(ngrDate); }}
              className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-gray-200 hover:border-emerald-300 rounded-sm text-[13px]"
              data-testid="refresh-referrals"
            >
              <RefreshCw size={13} className={(loading || ngrLoading) ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        }
      />

      {/* NGR Disabled banner */}
      {!ngrCfg.enabled && (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-[12px] text-amber-800 flex items-center gap-2" data-testid="ngr-disabled-banner">
          <AlertCircle size={14} /> NGR commission is <strong>disabled</strong>. Open NGR Config to enable.
        </div>
      )}

      {/* ============ NGR Daily Settlement Dashboard ============ */}
      <NgrSettlementPanel
        date={ngrDate}
        setDate={setNgrDate}
        report={ngrReport}
        settlements={ngrSettlements}
        loading={ngrLoading}
        running={ngrRunning}
        onReload={() => loadNgr(ngrDate)}
        onRunNow={runNgrNow}
        pct={ngrCfg.pct}
      />

      {/* ============ Real Cash Flow Report (informational) ============ */}
      <CashFlowReport date={ngrDate} />

      {/* ============ Top Referrers (lifetime stats) ============ */}
      <TopReferrersPanel />

      {/* Legacy loss-commission status banner (deprecated) */}
      {!lossCfg.enabled && (
        <div className="mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-sm text-[11px] text-gray-500" data-testid="loss-disabled-banner">
          ℹ️ Legacy Net-Loss commission is disabled (replaced by NGR system above).
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3 mb-4">
        <StatCard label="Active Referrers" value={stats.total_referrers ?? 0} tone="emerald" Icon={Network} testId="stat-referrers" />
        <StatCard label="Total Referees" value={stats.total_referees ?? 0} tone="sky" testId="stat-referees" />
        <StatCard label="Deposits" value={fmtBDT(stats.total_deposits ?? 0)} tone="violet" testId="stat-deposits" />
        <StatCard label="Withdrawals" value={fmtBDT(stats.total_withdrawals ?? 0)} tone="amber" testId="stat-withdraws" />
        <StatCard label="Net Loss" value={fmtBDT(stats.total_loss ?? 0)} tone="rose" testId="stat-loss" />
        <StatCard label={`Commission @${lossCfg.pct}%`} value={fmtBDT(stats.commission_pending ?? 0)} tone="emerald" Icon={Coins} testId="stat-commission-pending" />
        <StatCard label="Total Paid" value={fmtBDT(stats.commission_paid ?? 0)} tone="sky" testId="stat-commission-paid" />
      </div>

      {/* Search */}
      <div className="bg-white border border-gray-200 rounded-sm p-3 mb-3 flex items-center gap-2">
        <Search size={14} className="text-gray-400 ml-1" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by username, name, refer code, phone, email or player id…"
          className="flex-1 h-9 px-2 outline-none text-sm bg-transparent"
          data-testid="referrals-search"
        />
        {search && (
          <button onClick={() => setSearch("")} className="h-7 w-7 inline-flex items-center justify-center text-gray-400 hover:text-gray-700 rounded-sm" data-testid="referrals-search-clear">
            <X size={14} />
          </button>
        )}
        <span className="text-[11px] text-gray-400 mr-2">{filtered.length} of {rows.length}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
              <tr>
                <th className="px-3 py-2 text-left">Referrer</th>
                <th className="px-3 py-2 text-left">Refer Code</th>
                <th className="px-3 py-2 text-right">Referees</th>
                <th className="px-3 py-2 text-right">Deposits</th>
                <th className="px-3 py-2 text-right">Withdrawals</th>
                <th className="px-3 py-2 text-right">Net Loss</th>
                <th className="px-3 py-2 text-right">Pending @{lossCfg.pct}%</th>
                <th className="px-3 py-2 text-right">Paid</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-gray-400">Loading referral graph…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-12 text-center text-gray-400">No referrers match your search.</td></tr>
              )}
              {!loading && visibleFiltered.map((r) => (
                <tr key={r.referrer_id || r.refer_code} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`referrer-row-${r.refer_code}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono text-emerald-700">{r.username || "(deleted)"}</div>
                    <div className="text-[11px] text-gray-500">{r.full_name || "—"}{r.player_id ? ` · #${r.player_id}` : ""}</div>
                    <div className="text-[10px] text-gray-400">{r.phone || "—"} · {r.email || ""}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[12px] text-purple-700">{r.refer_code}</td>
                  <td className="px-3 py-2 text-right">
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold tabular">
                      {r.referee_count}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular text-emerald-700 font-semibold">{fmtBDT(r.deposit_total)}</td>
                  <td className="px-3 py-2 text-right tabular text-amber-700">{fmtBDT(r.withdraw_total)}</td>
                  <td className="px-3 py-2 text-right tabular text-rose-700 font-semibold">{fmtBDT(r.loss_total ?? 0)}</td>
                  <td className="px-3 py-2 text-right tabular font-bold text-emerald-700">{fmtBDT(r.commission_pending ?? 0)}</td>
                  <td className="px-3 py-2 text-right tabular text-gray-600">{fmtBDT(r.commission_paid ?? 0)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => payCommission(r)}
                        disabled={!r.commission_eligible || payingId === r.referrer_id}
                        title={r.commission_eligible ? `Pay ৳${(r.commission_pending || 0).toFixed(2)} to ${r.username}` : "Nothing to pay (or below threshold)"}
                        className="h-8 px-2 inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-sm text-[12px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`pay-commission-${r.refer_code}`}
                      >
                        <Coins size={12} /> {payingId === r.referrer_id ? "…" : "Pay"}
                      </button>
                      <button
                        onClick={() => openHistory(r)}
                        title="Payout history"
                        disabled={!r.referrer_id}
                        className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 hover:border-sky-300 hover:text-sky-600 rounded-sm disabled:opacity-40"
                        data-testid={`history-commission-${r.refer_code}`}
                      >
                        <HistoryIcon size={12} />
                      </button>
                      <button
                        onClick={() => openDrill(r)}
                        title="View referees"
                        disabled={!r.referrer_id}
                        className="h-8 px-2 inline-flex items-center gap-1 border border-gray-200 hover:border-emerald-300 hover:text-emerald-600 rounded-sm text-[12px] disabled:opacity-40"
                        data-testid={`drill-referees-${r.refer_code}`}
                      >
                        View <ChevronRight size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          total={filtered.length}
          page={page}
          pageSize={PAGE_SIZE}
          onChange={setPage}
          testIdPrefix="ref-list-pg"
        />
      </div>

      {showSettings && <LossCommissionSettings cfg={lossCfg} onClose={() => setShowSettings(false)} onSave={saveSettings} />}
      {showNgrSettings && <NgrSettingsModal cfg={ngrCfg} onClose={() => setShowNgrSettings(false)} onSave={saveNgrCfg} />}
      {historyDrawer && <CommissionHistoryDrawer data={historyDrawer} onClose={() => setHistoryDrawer(null)} />}

      {drill && (
        <ReferralDrillDrawer
          loading={drillLoading}
          data={drill}
          onClose={() => setDrill(null)}
          onOpenTx={openTransactions}
        />
      )}
      {txDrawer && (
        <TransactionsDrawer
          loading={txLoading}
          data={txDrawer}
          onClose={() => setTxDrawer(null)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, tone = "emerald", Icon, testId }) {
  const toneCls = {
    emerald: "border-emerald-100 text-emerald-700",
    sky: "border-sky-100 text-sky-700",
    violet: "border-violet-100 text-violet-700",
    amber: "border-amber-100 text-amber-700",
    rose: "border-rose-100 text-rose-700",
  }[tone] || "border-gray-100 text-gray-700";
  return (
    <div className={`bg-white border ${toneCls.split(" ")[0]} rounded-sm p-3`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
        {Icon && <Icon size={14} className={toneCls.split(" ")[1]} />}
      </div>
      <div className={`text-xl font-bold mt-1 tabular ${toneCls.split(" ")[1]}`}>{value}</div>
    </div>
  );
}

function ReferralDrillDrawer({ loading, data, onClose, onOpenTx }) {
  const r = data?.referrer || {};
  const upline = data?.upline;
  const referees = data?.referees || [];
  const totals = data?.totals || {};
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="referral-drill-drawer">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Referrer</div>
            <h3 className="text-lg font-bold text-gray-900 truncate">{r.username || "—"} <span className="text-[11px] font-mono text-purple-700 ml-2">{r.refer_code}</span></h3>
            <div className="text-xs text-gray-500 mt-0.5">{r.full_name || "—"} · {r.email || "—"} · {r.phone || "—"}</div>
            {upline && (
              <div className="mt-2 text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-violet-50 border border-violet-200 text-violet-700">
                Upline: <span className="font-mono">{upline.username}</span> ({upline.refer_code})
              </div>
            )}
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center text-gray-400 hover:text-gray-700 rounded-sm" data-testid="referral-drill-close">
            <X size={16} />
          </button>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-5 gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
          <MiniStat label="Referees" value={totals.count ?? 0} />
          <MiniStat label="Total Deposits" value={fmtBDT(totals.deposit_total ?? 0)} tone="emerald" />
          <MiniStat label="Total Withdrawals" value={fmtBDT(totals.withdraw_total ?? 0)} tone="amber" />
          <MiniStat label="Net Value" value={fmtBDT(totals.net_value ?? 0)} tone={(totals.net_value ?? 0) >= 0 ? "emerald" : "rose"} />
          <MiniStat label="Net Loss" value={fmtBDT(totals.net_loss ?? 0)} tone={(totals.net_loss ?? 0) >= 0 ? "rose" : "emerald"} />
        </div>

        {/* Referees */}
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold mb-2">Direct Referees ({referees.length})</div>
          {loading && <div className="text-center text-gray-400 py-8">Loading referees…</div>}
          {!loading && referees.length === 0 && (
            <div className="text-center text-gray-400 py-8 border border-dashed border-gray-200 rounded-sm">
              এই user এখনো কাউকে refer করেনি।
            </div>
          )}
          {!loading && referees.map((u) => (
            <div key={u.user_id} className="border border-gray-200 rounded-sm p-3 mb-2 hover:border-emerald-300" data-testid={`referee-${u.username}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-emerald-700 text-sm">{u.username}</span>
                    {u.player_id && <span className="text-[10px] text-gray-400">#{u.player_id}</span>}
                    {u.is_blocked && <span className="text-[10px] uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-sm font-bold">Blocked</span>}
                    {u.is_affiliate && <span className="text-[10px] uppercase tracking-wider bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded-sm font-bold">Affiliate</span>}
                  </div>
                  <div className="text-[11px] text-gray-500 truncate">{u.full_name || "—"} · {u.email || "—"} · {u.phone || "—"}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">Joined {fmtBD(u.joined_at)} · Balance {fmtBDT(u.balance)}</div>
                </div>
                <button
                  onClick={() => onOpenTx(u)}
                  className="shrink-0 h-8 px-3 inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-sm text-[12px] font-semibold"
                  data-testid={`open-tx-${u.username}`}
                >
                  <ExternalLink size={12} /> Transactions
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 pt-2 border-t border-gray-100 text-[12px]">
                <Stat icon={ArrowDownToLine} tone="emerald" label="Deposits" v1={fmtBDT(u.deposit_total)} v2={`${u.deposit_count} txn`} />
                <Stat icon={ArrowUpFromLine} tone="amber" label="Withdrawals" v1={fmtBDT(u.withdraw_total)} v2={`${u.withdraw_count} txn`} />
                <Stat icon={Wallet} tone={u.net_value >= 0 ? "emerald" : "rose"} label="Net" v1={fmtBDT(u.net_value)} v2={`${u.bet_count} bets`} />
                <Stat icon={Coins} tone={(u.net_loss ?? 0) >= 0 ? "rose" : "emerald"} label="Net Loss" v1={fmtBDT(u.net_loss ?? 0)} v2="dep − wd − bal" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TransactionsDrawer({ loading, data, onClose }) {
  const u = data?.user || {};
  const items = data?.items || [];
  const s = data?.stats || {};
  const siteStats = s.site || {};
  const agentStats = s.agent || {};
  const siteItems = items.filter((t) => (t.source || "site") === "site");
  const agentItems = items.filter((t) => t.source === "agent");
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="referee-transactions-drawer">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Transactions of</div>
            <h3 className="text-lg font-bold text-gray-900">{u.username} <span className="text-[11px] text-gray-400 font-mono">#{u.player_id}</span></h3>
            <div className="text-xs text-gray-500">{u.email || "—"} · {u.phone || "—"}</div>
            <div className="text-[10px] text-gray-400 mt-1">Showing only successful Deposits & Withdrawals</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center text-gray-400 hover:text-gray-700 rounded-sm" data-testid="tx-drawer-close">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 px-5 py-3 bg-gray-50 border-b border-gray-200">
          <MiniStat label="Deposits" value={`${fmtBDT(s.deposit_total ?? 0)} · ${s.deposit_count ?? 0} txn`} tone="emerald" />
          <MiniStat label="Withdrawals" value={`${fmtBDT(s.withdraw_total ?? 0)} · ${s.withdraw_count ?? 0} txn`} tone="amber" />
          <MiniStat label="Net" value={fmtBDT(s.net_value ?? 0)} tone={(s.net_value ?? 0) >= 0 ? "emerald" : "rose"} />
          <MiniStat label="Balance" value={fmtBDT(s.balance ?? 0)} tone="gray" />
          <MiniStat label="Net Loss" value={fmtBDT(s.net_loss ?? 0)} tone={(s.net_loss ?? 0) >= 0 ? "rose" : "emerald"} />
        </div>

        <div className="p-5 space-y-6">
          {loading && <div className="text-center text-gray-400 py-8">Loading transactions…</div>}
          {!loading && items.length === 0 && (
            <div className="text-center text-gray-400 py-8 border border-dashed border-gray-200 rounded-sm">
              এই user এর কোনো successful transaction পাওয়া যায়নি।
            </div>
          )}

          {!loading && items.length > 0 && (
            <>
              <SectionTable
                title="Site"
                subtitle="User এর নিজের ডিপোজিট / উইথড্র (Successful only)"
                tone="emerald"
                stats={siteStats}
                items={siteItems}
                showAgent={false}
                testIdPrefix="site"
              />
              <SectionTable
                title="Agent"
                subtitle="Agent এর মাধ্যমে ডিপোজিট / উইথড্র (Successful only)"
                tone="indigo"
                stats={agentStats}
                items={agentItems}
                showAgent={true}
                testIdPrefix="agent"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionTable({ title, subtitle, tone, stats, items, showAgent, testIdPrefix }) {
  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    indigo: "border-indigo-200 bg-indigo-50/40",
  }[tone] || "border-gray-200 bg-gray-50/40";
  const titleCls = {
    emerald: "text-emerald-700",
    indigo: "text-indigo-700",
  }[tone] || "text-gray-700";
  return (
    <div className={`border ${toneCls} rounded-sm`} data-testid={`${testIdPrefix}-section`}>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className={`text-sm font-bold uppercase tracking-wider ${titleCls}`}>{title}</div>
          <div className="text-[11px] text-gray-500">{subtitle}</div>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="text-emerald-700"><span className="font-semibold">Dep:</span> {fmtBDT(stats?.deposit_total ?? 0)} · {stats?.deposit_count ?? 0}</span>
          <span className="text-amber-700"><span className="font-semibold">Wd:</span> {fmtBDT(stats?.withdraw_total ?? 0)} · {stats?.withdraw_count ?? 0}</span>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-center text-gray-400 py-6 text-[12px]">কোনো {title.toLowerCase()} transaction নেই।</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500 font-bold border-b border-gray-200 bg-white/60">
              <tr>
                <th className="text-left py-2 px-3">Date (BDT)</th>
                <th className="text-left">Type</th>
                <th className="text-left">Method</th>
                {showAgent && <th className="text-left">Agent</th>}
                <th className="text-right pr-3">Amount</th>
                <th className="text-left">Status</th>
                <th className="text-left pr-3">Reference</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t, idx) => (
                <tr key={`${testIdPrefix}-${t.id || idx}`} className="border-b border-gray-100 last:border-0" data-testid={`${testIdPrefix}-tx-row-${t.id || idx}`}>
                  <td className="py-2 px-3 text-[11px] text-gray-500 whitespace-nowrap">{fmtBD(t.created_at)}</td>
                  <td>{t.type === "deposit" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700"><ArrowDownToLine size={12} /> Deposit</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700"><ArrowUpFromLine size={12} /> Withdrawal</span>
                  )}</td>
                  <td className="text-[12px] text-gray-600">{t.method || "—"}</td>
                  {showAgent && (
                    <td className="text-[12px] text-indigo-700 font-semibold" data-testid={`${testIdPrefix}-tx-agent-${t.id}`}>
                      {t.agent_username || t.agent_name || "—"}
                    </td>
                  )}
                  <td className="text-right tabular font-semibold pr-3">{fmtBDT(t.amount)}</td>
                  <td><StatusPill status={t.status} /></td>
                  <td className="text-[11px] text-gray-500 font-mono truncate max-w-[140px] pr-3">{t.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone = "gray" }) {
  const cls = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
    gray: "text-gray-700",
  }[tone] || "text-gray-700";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className={`text-sm font-bold tabular ${cls}`}>{value}</div>
    </div>
  );
}

function Stat({ icon: Icon, tone, label, v1, v2 }) {
  const cls = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  }[tone] || "text-gray-700";
  return (
    <div className="flex items-center gap-2">
      <div className={`h-7 w-7 inline-flex items-center justify-center rounded-sm bg-gray-50 border border-gray-100 ${cls}`}>
        <Icon size={12} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
        <div className={`text-[12px] font-bold tabular ${cls}`}>{v1} <span className="text-[10px] text-gray-400 font-normal">· {v2}</span></div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || "").toLowerCase();
  const map = {
    completed: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    approved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    success: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
    pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
    processing: { cls: "bg-sky-50 text-sky-700 border-sky-200", Icon: Clock },
    rejected: { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle },
    failed: { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle },
    cancelled: { cls: "bg-gray-100 text-gray-600 border-gray-200", Icon: XCircle },
  }[s] || { cls: "bg-gray-100 text-gray-600 border-gray-200", Icon: Clock };
  const Icon = map.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold border px-2 py-0.5 rounded-sm ${map.cls}`}>
      <Icon size={10} /> {s || "—"}
    </span>
  );
}


function LossCommissionSettings({ cfg, onClose, onSave }) {
  const [form, setForm] = useState({
    enabled: !!cfg.enabled,
    pct: Number(cfg.pct ?? 5),
    min_loss_threshold: Number(cfg.min_loss_threshold ?? 0),
    min_payout: Number(cfg.min_payout ?? 0),
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (form.pct < 0 || form.pct > 100) {
      toast.error("Commission % must be between 0 and 100");
      return;
    }
    setBusy(true);
    await onSave(form);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="loss-settings-modal">
        <h3 className="text-lg font-bold mb-1 flex items-center gap-2"><Coins size={18} className="text-emerald-600" /> Referral Loss Commission</h3>
        <p className="text-xs text-gray-500 mb-5 leading-relaxed">
          যখন কোনো referred user deposit করে এবং সেই টাকা থেকে loss করে (i.e. deposits − withdrawals − current balance), তখন এই % তার referrer এর wallet এ credit করা হবে।
        </p>

        <div className="space-y-4">
          {/* Enabled toggle */}
          <label className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50">
            <div>
              <div className="text-sm font-semibold">Enable loss commission</div>
              <div className="text-[11px] text-gray-500">Off হলে admin Pay বাটন ক্লিক করলেও commission credit হবে না।</div>
            </div>
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-5 w-5 accent-emerald-600" data-testid="loss-enabled-toggle" />
          </label>

          {/* % */}
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Commission % of net loss</label>
            <div className="relative">
              <input type="number" step="0.1" min="0" max="100" value={form.pct} onChange={(e) => setForm({ ...form, pct: parseFloat(e.target.value) || 0 })} className="w-full h-10 pl-3 pr-8 border border-gray-200 rounded-sm text-sm tabular" data-testid="loss-pct-input" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
            </div>
            <div className="text-[11px] text-gray-500 mt-1">Example: 10% means a referee with ৳1,000 net loss earns the referrer ৳100.</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Minimum loss threshold (BDT)</label>
              <input type="number" step="0.01" min="0" value={form.min_loss_threshold} onChange={(e) => setForm({ ...form, min_loss_threshold: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="loss-min-threshold" />
              <div className="text-[11px] text-gray-500 mt-1">Below this total loss, no commission accrues.</div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Minimum payout (BDT)</label>
              <input type="number" step="0.01" min="0" value={form.min_payout} onChange={(e) => setForm({ ...form, min_payout: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="loss-min-payout" />
              <div className="text-[11px] text-gray-500 mt-1">Pay button blocked below this amount.</div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="h-9 px-4 border border-gray-200 rounded-sm text-sm" data-testid="loss-settings-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="h-9 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold disabled:opacity-50" data-testid="loss-settings-save">
            {busy ? "Saving..." : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}


function CommissionHistoryDrawer({ data, onClose }) {
  const r = data?.referrer || {};
  const items = data?.items || [];
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="commission-history-drawer">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Loss commission payouts</div>
            <h3 className="text-lg font-bold text-gray-900">{r.username} <span className="text-[11px] font-mono text-purple-700 ml-2">{r.refer_code}</span></h3>
            <div className="text-xs text-gray-500">Total paid so far: <span className="font-bold text-emerald-700">{fmtBDT(data.total_paid || 0)}</span> · {items.length} payouts</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center text-gray-400 hover:text-gray-700 rounded-sm" data-testid="history-drawer-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          {data.loading && <div className="text-center text-gray-400 py-8">Loading…</div>}
          {!data.loading && items.length === 0 && (
            <div className="text-center text-gray-400 py-8 border border-dashed border-gray-200 rounded-sm">এখনো কোনো commission payout হয়নি।</div>
          )}
          {!data.loading && items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-gray-500 font-bold border-b border-gray-200">
                <tr>
                  <th className="text-left py-2">Date (BDT)</th>
                  <th className="text-right">Loss snapshot</th>
                  <th className="text-right">% rate</th>
                  <th className="text-right">Amount</th>
                  <th className="text-left">Note</th>
                  <th className="text-left">By</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-gray-100">
                    <td className="py-2 text-[11px] text-gray-500">{fmtBD(p.created_at)}</td>
                    <td className="text-right tabular text-rose-700">{fmtBDT(p.loss_total_at_payout)}</td>
                    <td className="text-right tabular text-purple-700">{(p.commission_pct_at_payout || 0).toFixed(1)}%</td>
                    <td className="text-right tabular text-emerald-700 font-bold">{fmtBDT(p.amount)}</td>
                    <td className="text-[11px] text-gray-600 max-w-[180px] truncate" title={p.note}>{p.note || "—"}</td>
                    <td className="text-[11px] text-gray-500">{p.admin_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


/* ============================================================
 * NGR SETTLEMENT DASHBOARD (new)
 * ============================================================ */
function NgrSettlementPanel({ date, setDate, report, settlements, loading, running, onReload, onRunNow, pct }) {
  const summary = report?.summary || { total_commission: 0, total_ngr: 0, credited_count: 0, total_reports: 0 };
  const items = report?.items || [];
  const lastRun = settlements?.[0];

  return (
    <div className="mb-5 bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-md overflow-hidden" data-testid="ngr-settlement-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-emerald-200 bg-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-emerald-100 grid place-items-center text-emerald-700">
            <Activity size={15} />
          </div>
          <div>
            <div className="text-[13px] font-bold text-gray-800">NGR Daily Settlement</div>
            <div className="text-[11px] text-gray-500">Auto-credits referrers @{pct}% NGR every রাত ১২টা (00:00 BD)</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 text-[11px] text-gray-500">
            <Calendar size={12} />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-8 px-2 border border-gray-200 rounded-sm text-[12px] font-mono"
              data-testid="ngr-date-picker"
            />
            <button
              onClick={onReload}
              className="h-8 px-2 border border-gray-200 hover:border-emerald-300 rounded-sm text-[12px] inline-flex items-center gap-1"
              data-testid="ngr-date-load"
            >
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Load
            </button>
          </div>
          <button
            onClick={onRunNow}
            disabled={running}
            className="h-8 px-3 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-[12px] font-semibold disabled:opacity-50"
            data-testid="ngr-run-now"
            title="Manually settle (idempotent) for selected date"
          >
            <PlayCircle size={12} /> {running ? "Running…" : "Run Now"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <NgrStatCard label="Settlement date" value={date} tone="emerald" Icon={Calendar} />
        <NgrStatCard label="Commission paid" value={fmtBDT(summary.total_commission)} tone="emerald" Icon={Coins} />
        <NgrStatCard label="Total NGR" value={fmtBDT(summary.total_ngr)} tone="violet" Icon={TrendingUp} />
        <NgrStatCard label="Referrers credited" value={summary.credited_count} tone="sky" Icon={CheckCircle2} />
        <NgrStatCard label="Total reports" value={summary.total_reports} tone="amber" Icon={Activity} />
      </div>

      {/* Per-referee daily reports + Settlement logs (two columns) */}
      <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4">
        {/* Daily reports */}
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 text-[11px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-2">
            <Activity size={11} /> Per-Referee NGR breakdown — {date}
            <span className="ml-auto text-gray-400 text-[10.5px] font-medium normal-case">{items.length} rows</span>
          </div>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-[12px]" data-testid="ngr-daily-reports-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-bold sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Referee</th>
                  <th className="px-3 py-2 text-left">Referrer</th>
                  <th className="px-3 py-2 text-right">Bets</th>
                  <th className="px-3 py-2 text-right">Wins</th>
                  <th className="px-3 py-2 text-right">Bonus+CB</th>
                  <th className="px-3 py-2 text-right">NGR</th>
                  <th className="px-3 py-2 text-right">Commission</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400 text-[12px]">
                    No reports for {date}. Click &quot;Run Now&quot; to settle, or pick another date.
                  </td></tr>
                )}
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-emerald-50/40" data-testid={`ngr-report-${r.referee_id}`}>
                    <td className="px-3 py-2 font-mono text-[11.5px]">@{r.referee_username}</td>
                    <td className="px-3 py-2 font-mono text-[11.5px] text-emerald-700">@{r.referrer_username}</td>
                    <td className="px-3 py-2 text-right tabular text-gray-700">{fmtBDT(r.total_bets)}</td>
                    <td className="px-3 py-2 text-right tabular text-gray-700">{fmtBDT(r.total_wins)}</td>
                    <td className="px-3 py-2 text-right tabular text-amber-700">{fmtBDT((r.bonuses || 0) + (r.cashback || 0))}</td>
                    <td className={`px-3 py-2 text-right tabular font-semibold ${r.ngr > 0 ? "text-rose-700" : "text-gray-400"}`}>{fmtBDT(r.ngr)}</td>
                    <td className="px-3 py-2 text-right tabular font-bold text-emerald-700">
                      {r.commission > 0 ? `+${fmtBDT(r.commission)}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Settlement logs */}
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 text-[11px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-2">
            <HistoryIcon size={11} /> Last 14 settlement runs
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
            {settlements.length === 0 && (
              <div className="px-3 py-8 text-center text-gray-400 text-[12px]">No settlement history yet.</div>
            )}
            {settlements.map((s) => (
              <div key={s.id || s.settlement_date} className="px-3 py-2.5 hover:bg-emerald-50/40 cursor-pointer" onClick={() => setDate(s.settlement_date)} data-testid={`ngr-settlement-${s.settlement_date}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[12px] text-emerald-800 font-bold">{s.settlement_date}</div>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                    s.status === "completed" ? "bg-emerald-50 text-emerald-700" :
                    s.status === "running" ? "bg-amber-50 text-amber-700" :
                    "bg-gray-100 text-gray-600"
                  }`}>{s.status}</span>
                </div>
                <div className="mt-1 grid grid-cols-3 gap-2 text-[10.5px] text-gray-600">
                  <div>Credited: <b className="text-emerald-700">{s.commissions_credited || 0}</b></div>
                  <div>Total: <b className="text-emerald-700">৳{(s.total_commission_paid || 0).toFixed(0)}</b></div>
                  <div>Errors: <b className={s.errors ? "text-rose-700" : "text-gray-500"}>{s.errors || 0}</b></div>
                </div>
              </div>
            ))}
          </div>
          {lastRun && (
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 text-[10.5px] text-gray-500">
              Last run: <span className="font-mono">{new Date(lastRun.ended_at || lastRun.started_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NgrStatCard({ label, value, tone, Icon }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
    amber: "border-amber-200 bg-amber-50",
    violet: "border-violet-200 bg-violet-50",
  };
  const iconC = {
    emerald: "text-emerald-600", sky: "text-sky-600", amber: "text-amber-600", violet: "text-violet-600",
  };
  return (
    <div className={`px-3 py-2.5 rounded-sm border ${tones[tone] || tones.emerald}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
        {Icon && <Icon size={12} className={iconC[tone]} />}
      </div>
      <div className="text-[15px] font-extrabold tabular text-gray-800 mt-1">{value}</div>
    </div>
  );
}

function NgrSettingsModal({ cfg, onClose, onSave }) {
  const [enabled, setEnabled] = useState(!!cfg.enabled);
  const [pct, setPct] = useState(String(cfg.pct ?? 10));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = parseFloat(pct);
    if (Number.isNaN(v) || v < 0 || v > 100) return toast.error("Percentage must be 0–100");
    setSaving(true);
    await onSave({ enabled, pct: v });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="ngr-settings-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-md shadow-2xl border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-gray-800"><Settings size={14} className="text-emerald-600" /> NGR Commission Config</div>
          <button type="button" onClick={onClose} className="h-7 w-7 grid place-items-center text-gray-400 hover:text-gray-700 rounded-sm"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-4 text-[13px]">
          <label className="flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-gray-800">System Enabled</div>
              <div className="text-[11px] text-gray-500">If off, the 12:00 AM daily job becomes a no-op.</div>
            </div>
            <button type="button" onClick={() => setEnabled(!enabled)} data-testid="ngr-cfg-toggle"
              className={`w-11 h-6 rounded-full relative transition ${enabled ? "bg-emerald-500" : "bg-gray-300"}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition ${enabled ? "left-5" : "left-0.5"}`} />
            </button>
          </label>
          <div>
            <div className="font-semibold text-gray-800 mb-1">Commission % of NGR</div>
            <input
              type="number" step="0.1" min="0" max="100"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-[15px] font-mono focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 outline-none"
              data-testid="ngr-cfg-pct"
            />
            <div className="text-[11px] text-gray-500 mt-1">
              NGR = Bets − Wins − Bonuses − Cashback. Commission = max(0, NGR) × {pct || 0}%.
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-3 text-[13px] text-gray-600 hover:text-gray-800">Cancel</button>
          <button type="submit" disabled={saving} className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-[13px] font-semibold disabled:opacity-50" data-testid="ngr-cfg-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ============================================================
 * REAL CASH FLOW REPORT (informational only — not used in NGR)
 * Shows successful site + agent deposits/withdrawals per referee.
 * ============================================================ */
function CashFlowReport({ date }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localDate, setLocalDate] = useState(date);

  // Sync external date prop into local
  useEffect(() => { setLocalDate(date); }, [date]);

  const load = async (d = localDate) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/referral-ngr/cash-flow", { params: { date: d } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cash flow");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(localDate); /* eslint-disable-next-line */ }, [localDate]);

  const summary = data?.summary || {};
  const items = data?.items || [];

  return (
    <div className="mb-5 bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="cash-flow-report">
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-violet-100 grid place-items-center text-violet-700">
            <Wallet size={15} />
          </div>
          <div>
            <div className="text-[13px] font-bold text-gray-800">Real Cash Flow · {localDate}</div>
            <div className="text-[11px] text-gray-500">Successful site + agent deposits/withdrawals — informational only, not used in NGR</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={localDate}
            onChange={(e) => setLocalDate(e.target.value)}
            className="h-8 px-2 border border-gray-200 rounded-sm text-[12px] font-mono"
            data-testid="cashflow-date-picker"
          />
          <button
            onClick={() => load(localDate)}
            className="h-8 px-2 border border-gray-200 hover:border-violet-300 rounded-sm text-[12px] inline-flex items-center gap-1"
            data-testid="cashflow-reload"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Reload
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="px-4 py-3 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <NgrStatCard label="Site Deposit" value={`${fmtBDT(summary.site_deposit)} (${summary.site_deposit_count || 0})`} tone="emerald" Icon={ArrowDownToLine} />
        <NgrStatCard label="Site Withdraw" value={`${fmtBDT(summary.site_withdraw)} (${summary.site_withdraw_count || 0})`} tone="amber" Icon={ArrowUpFromLine} />
        <NgrStatCard label="Agent Deposit" value={`${fmtBDT(summary.agent_deposit)} (${summary.agent_deposit_count || 0})`} tone="sky" Icon={ArrowDownToLine} />
        <NgrStatCard label="Agent Withdraw" value={`${fmtBDT(summary.agent_withdraw)} (${summary.agent_withdraw_count || 0})`} tone="violet" Icon={ArrowUpFromLine} />
        <NgrStatCard label="Net Flow" value={fmtBDT(summary.net_flow)} tone={summary.net_flow >= 0 ? "emerald" : "amber"} Icon={TrendingUp} />
      </div>

      {/* Per-referee table */}
      <div className="px-4 pb-4">
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 text-[11px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-2">
            <Activity size={11} /> Per-Referee real cash activity
            <span className="ml-auto text-gray-400 text-[10.5px] font-medium normal-case">{items.length} active</span>
          </div>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-[12px]" data-testid="cashflow-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-bold sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Referee</th>
                  <th className="px-3 py-2 text-left">Referrer</th>
                  <th className="px-3 py-2 text-right">Site Dep</th>
                  <th className="px-3 py-2 text-right">Site Wd</th>
                  <th className="px-3 py-2 text-right">Agent Dep</th>
                  <th className="px-3 py-2 text-right">Agent Wd</th>
                  <th className="px-3 py-2 text-right">Money In</th>
                  <th className="px-3 py-2 text-right">Money Out</th>
                  <th className="px-3 py-2 text-right">Net</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400 text-[12px]">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400 text-[12px]">
                    No real-money activity from any referee on {localDate}.
                  </td></tr>
                )}
                {!loading && items.map((r) => (
                  <tr key={r.referee_id} className="border-t border-gray-100 hover:bg-violet-50/30" data-testid={`cashflow-row-${r.referee_id}`}>
                    <td className="px-3 py-2 font-mono text-[11.5px]">@{r.referee_username}</td>
                    <td className="px-3 py-2 font-mono text-[11.5px] text-emerald-700">{r.referrer_username ? `@${r.referrer_username}` : "—"}</td>
                    <td className="px-3 py-2 text-right tabular text-emerald-700">
                      {r.site_deposit > 0 ? fmtBDT(r.site_deposit) : "—"}
                      {r.site_deposit_count > 0 && <span className="text-[9.5px] text-gray-400 ml-1">×{r.site_deposit_count}</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular text-amber-700">
                      {r.site_withdraw > 0 ? fmtBDT(r.site_withdraw) : "—"}
                      {r.site_withdraw_count > 0 && <span className="text-[9.5px] text-gray-400 ml-1">×{r.site_withdraw_count}</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular text-sky-700">
                      {r.agent_deposit > 0 ? fmtBDT(r.agent_deposit) : "—"}
                      {r.agent_deposit_count > 0 && <span className="text-[9.5px] text-gray-400 ml-1">×{r.agent_deposit_count}</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular text-violet-700">
                      {r.agent_withdraw > 0 ? fmtBDT(r.agent_withdraw) : "—"}
                      {r.agent_withdraw_count > 0 && <span className="text-[9.5px] text-gray-400 ml-1">×{r.agent_withdraw_count}</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular font-semibold text-emerald-800">{fmtBDT(r.real_money_in)}</td>
                    <td className="px-3 py-2 text-right tabular font-semibold text-rose-700">{fmtBDT(r.real_money_out)}</td>
                    <td className={`px-3 py-2 text-right tabular font-bold ${r.net_flow >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtBDT(r.net_flow)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ============================================================
 * TOP REFERRERS — all users who have at least one referee.
 * Sorted by referees → lifetime commission. Uses
 * /admin/referral-ngr/users endpoint.
 * ============================================================ */
function TopReferrersPanel() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [sortKey, setSortKey] = useState("referees"); // referees | lifetime_commission | balance

  const load = async (p = 1, s = search) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/referral-ngr/users", {
        params: { search: s, limit: pageSize, skip: (p - 1) * pageSize },
      });
      setData(data || { items: [], total: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load referrers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(1, ""); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    load(1, search.trim());
  };

  const sorted = useMemo(() => {
    const items = [...(data.items || [])];
    items.sort((a, b) => {
      const av = Number(a[sortKey] || 0);
      const bv = Number(b[sortKey] || 0);
      return bv - av;
    });
    return items;
  }, [data.items, sortKey]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div className="mb-5 bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="top-referrers-panel">
      <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-amber-100 grid place-items-center text-amber-700">
            <Network size={15} />
          </div>
          <div>
            <div className="text-[13px] font-bold text-gray-800">Top Referrers · Lifetime stats</div>
            <div className="text-[11px] text-gray-500">সব user যারা কাউকে refer করেছে — referee count + lifetime commission</div>
          </div>
        </div>
        <form onSubmit={onSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="username / email / refer code"
              className="h-8 pl-7 pr-2 w-64 border border-gray-200 rounded-sm text-[12px] focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
              data-testid="top-referrers-search"
            />
          </div>
          <button type="submit" className="h-8 px-3 bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-[12px] font-semibold" data-testid="top-referrers-search-submit">
            Search
          </button>
          <button
            type="button"
            onClick={() => { setSearch(""); setPage(1); load(1, ""); }}
            className="h-8 px-2 border border-gray-200 hover:border-amber-300 rounded-sm text-[12px]"
            data-testid="top-referrers-clear"
          >
            Clear
          </button>
        </form>
      </div>

      {/* Sort tabs */}
      <div className="px-4 pt-2 flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold border-b border-gray-100">
        <span className="text-gray-400">Sort:</span>
        {[
          { k: "referees", label: "Referee count" },
          { k: "lifetime_commission", label: "Lifetime commission" },
          { k: "balance", label: "Wallet balance" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setSortKey(t.k)}
            className={`px-2.5 py-1.5 ${sortKey === t.k ? "text-amber-700 border-b-2 border-amber-500" : "text-gray-500 hover:text-gray-800 border-b-2 border-transparent"}`}
            data-testid={`top-referrers-sort-${t.k}`}
          >
            {t.label}
          </button>
        ))}
        <span className="ml-auto text-gray-400 text-[10.5px] font-medium normal-case py-1.5">{data.total} referrers</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="top-referrers-table">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
            <tr>
              <th className="px-3 py-2 text-left w-10">#</th>
              <th className="px-3 py-2 text-left">Referrer</th>
              <th className="px-3 py-2 text-left">Refer code</th>
              <th className="px-3 py-2 text-right">Referees</th>
              <th className="px-3 py-2 text-right">Credit events</th>
              <th className="px-3 py-2 text-right">Lifetime commission</th>
              <th className="px-3 py-2 text-right">Wallet balance</th>
              <th className="px-3 py-2 text-left">Email</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400 text-[12px]">Loading…</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400 text-[12px]">
                {search ? "No referrers match your search." : "No referrers in the system yet."}
              </td></tr>
            )}
            {!loading && sorted.map((u, i) => (
              <tr key={u.user_id} className="border-t border-gray-100 hover:bg-amber-50/30" data-testid={`top-referrer-${u.user_id}`}>
                <td className="px-3 py-2 text-gray-400 tabular">{(page - 1) * pageSize + i + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-mono text-[12.5px] font-semibold text-gray-800">@{u.username}</div>
                  {u.player_id && <div className="text-[10px] text-gray-400 font-mono">PID {u.player_id}</div>}
                </td>
                <td className="px-3 py-2 font-mono text-[11.5px] text-amber-700 font-bold">{u.refer_code || "—"}</td>
                <td className="px-3 py-2 text-right tabular font-bold text-emerald-700">{u.referees}</td>
                <td className="px-3 py-2 text-right tabular text-gray-600">{u.commission_events}</td>
                <td className="px-3 py-2 text-right tabular font-bold text-emerald-800">{fmtBDT(u.lifetime_commission)}</td>
                <td className="px-3 py-2 text-right tabular text-gray-700">{fmtBDT(u.balance)}</td>
                <td className="px-3 py-2 text-[11.5px] text-gray-500 truncate max-w-[200px]">{u.email || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-[11.5px]">
          <div className="text-gray-500">Page {page} of {totalPages}</div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => { const p = Math.max(1, page - 1); setPage(p); load(p, search); }}
              disabled={page <= 1}
              className="h-8 px-3 border border-gray-200 rounded-sm hover:border-amber-300 disabled:opacity-40"
              data-testid="top-referrers-prev"
            >Prev</button>
            <button
              onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); load(p, search); }}
              disabled={page >= totalPages}
              className="h-8 px-3 border border-gray-200 rounded-sm hover:border-amber-300 disabled:opacity-40"
              data-testid="top-referrers-next"
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

